import type { Chiffrage, CreateChiffrageInput } from '../types';

// Configuration de l'URL de l'API
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const API_BASE = API_BASE_URL ? `${API_BASE_URL}/api` : '/api';

// Vérification de la configuration
export const isApiConfigured = (): boolean => {
    // En production, VITE_API_URL doit être défini
    if (import.meta.env.PROD && !import.meta.env.VITE_API_URL) {
        return false;
    }
    return true;
};

export const getApiUrl = (): string => API_BASE;

// Helper pour gérer les erreurs de réponse
async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        // Lire le texte brut pour debug (évite "Unexpected token <")
        const text = await response.text();
        console.error(`❌ Erreur API (${response.status}):`, text);

        // Essayer de parser comme JSON pour un message d'erreur propre
        try {
            const error = JSON.parse(text);
            throw new Error(error.error || error.message || `Erreur ${response.status}`);
        } catch {
            // Si ce n'est pas du JSON (ex: page HTML 404)
            if (text.includes('<!DOCTYPE') || text.includes('<html')) {
                throw new Error(`L'API n'est pas accessible. Vérifiez VITE_API_URL (${API_BASE})`);
            }
            throw new Error(`Erreur ${response.status}: ${text.slice(0, 100)}`);
        }
    }

    return response.json();
}

// Récupérer toutes les lignes avec filtres optionnels
export async function fetchChiffrages(params?: {
    univers?: string;
    statut?: string;
    search?: string;
}): Promise<Chiffrage[]> {
    const searchParams = new URLSearchParams();

    if (params?.univers && params.univers !== 'Tous') {
        searchParams.set('univers', params.univers);
    }
    if (params?.statut && params.statut !== 'Tous') {
        searchParams.set('statut', params.statut);
    }
    if (params?.search) {
        searchParams.set('search', params.search);
    }

    const url = `${API_BASE}/chiffrages?${searchParams.toString()}`;
    const response = await fetch(url);
    return handleResponse<Chiffrage[]>(response);
}

// Créer une nouvelle ligne
export async function createChiffrage(input: CreateChiffrageInput): Promise<Chiffrage> {
    const response = await fetch(`${API_BASE}/chiffrages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
    });
    return handleResponse<Chiffrage>(response);
}

// Mettre à jour le statut et envoyer un email
export async function updateStatusAndSendEmail(
    chiffrageId: number,
    statut: 'Attribués' | 'Partiel',
    numeroOrchestra: string,
    joursAttribues?: number,
    file?: File,
    lienJira?: string
): Promise<{ success: boolean; chiffrage: Chiffrage; email: { success: boolean; message: string } }> {
    const formData = new FormData();
    formData.append('chiffrageId', chiffrageId.toString());
    formData.append('statut', statut);
    formData.append('numero_orchestra', numeroOrchestra);

    if (joursAttribues !== undefined) {
        formData.append('jours_attribues', joursAttribues.toString());
    }

    if (file) {
        formData.append('attachment', file);
    }

    if (lienJira) {
        formData.append('lien_jira', lienJira);
    }

    const response = await fetch(`${API_BASE}/send-email`, {
        method: 'POST',
        body: formData,
    });
    return handleResponse(response);
}

// Supprimer une ligne
export async function deleteChiffrage(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/chiffrages/${id}`, {
        method: 'DELETE',
    });

    if (!response.ok) {
        const text = await response.text();
        console.error(`❌ Erreur suppression:`, text);
        throw new Error('Erreur lors de la suppression');
    }
}

// Exporter les données en JSON
export function exportData(): void {
    window.open(`${API_BASE}/chiffrages/export`, '_blank');
}

// Vérifier la santé du serveur
export async function checkHealth(): Promise<boolean> {
    try {
        const response = await fetch(`${API_BASE}/health`);
        return response.ok;
    } catch {
        return false;
    }
}
